/**
 * TOSS Web Application — горизонтальная геометрия выпадающего слоя
 *
 * Отдельный модуль без JSX, а не пара функций рядом с компонентом: экспорт не-компонента из
 * `.tsx` ломает Fast Refresh (`react-refresh/only-export-components`), и по этой же причине из
 * `DatePresetFilter.tsx` в своё время уехал `datePresets.ts`. Побочная выгода в том, что
 * позицию стало чем проверить: в jsdom `getBoundingClientRect` и `offsetWidth` всегда нулевые,
 * так что рендером эта математика не тестируется никак.
 */

/** Отступ от края окна, чтобы слой не прилипал к нему вплотную. */
export const EDGE_GAP = 8;

export interface HorizontalPositionInput {
  placement: 'bottom-end' | 'bottom-start';
  /** Левый и правый края триггера в координатах окна. */
  triggerLeft: number;
  triggerRight: number;
  /** `0` до первой отрисовки слоя: ширину ещё не у чего спросить. */
  dropdownWidth: number;
  viewportWidth: number;
}

export interface HorizontalPosition {
  left?: number;
  right?: number;
  /** Выставляется, ТОЛЬКО когда слой шире свободного места, — см. докблок ниже. */
  maxWidth?: number;
}

/**
 * Слой всегда привязан к триггеру и всегда зажат в границы экрана.
 *
 * **Ветки «ниже 480 px растянуть от края до края» здесь больше нет, и она никогда не работала.**
 * Она ставила слою `left: 1rem; right: 1rem` — но ширину задаёт КАЖДАЯ вызывающая сторона
 * классом (`w-52`, `w-56`, `w-60`, `w-[360px]`, `w-[380px]`), а при заданной `width` браузер
 * `right` игнорирует. Вместо растяжения слой прилипал к ЛЕВОМУ краю экрана: кебаб карточки
 * шаблона стоит справа, а его меню открывалось в двухстах пикселях левее, поверх содержимого
 * карточки (стенд, 27.08.2026). К этой ветке трижды добавляли точечный опт-аут
 * `keepAnchoredOnNarrow` — чипу юрлица, кебабу заявки, кебабу ролей; кебабы шаблона и коллеги
 * его не получили и ехали влево.
 *
 * Зажимаются ОБА края. До этого `bottom-end` проверял только левый, а `bottom-start` только
 * правый — из-за чего поповер расшифровки оборудования (`w-[360px]`) на 360-пиксельном телефоне
 * вылезал за правый край.
 *
 * `maxWidth` появляется в стиле, ТОЛЬКО когда слой и правда шире свободного места. Инлайновый
 * стиль сильнее класса, и постоянный `maxWidth` отменил бы собственные `max-w-[calc(100vw-2rem)]`
 * у поповера уведомлений и у списка юрлица — а это их выбор ширины, а не страховка. Страховка
 * включается по `>=`, а не по `>`: после обрезки слой меряется ровно в `available`, и строгое
 * сравнение сняло бы ограничение, слой вырос бы обратно и заклинил бы на пересчёте при каждом
 * скролле.
 */
export function resolveHorizontalPosition({
  placement,
  triggerLeft,
  triggerRight,
  dropdownWidth,
  viewportWidth,
}: HorizontalPositionInput): HorizontalPosition {
  const available = Math.max(0, viewportWidth - EDGE_GAP * 2);

  // Первый проход: слоя ещё нет в DOM, ширина неизвестна. Зажимать по нулю нельзя — слой
  // прыгнул бы к краю и вернулся. Якорь сам по себе уже верен, ему не хватает только обрезки,
  // и её добавит повторный расчёт в `requestAnimationFrame`.
  if (dropdownWidth <= 0) {
    return placement === 'bottom-end'
      ? { right: Math.max(EDGE_GAP, viewportWidth - triggerRight) }
      : { left: Math.max(EDGE_GAP, triggerLeft) };
  }

  const preferredLeft = placement === 'bottom-end' ? triggerRight - dropdownWidth : triggerLeft;
  // Правее этого левый край ставить нельзя: слой вышел бы за правую границу окна. У слоя шире
  // экрана обе границы сходятся в `EDGE_GAP` — там его и дожимает `maxWidth`.
  const rightmostLeft = Math.max(EDGE_GAP, viewportWidth - EDGE_GAP - dropdownWidth);

  return {
    left: Math.min(Math.max(preferredLeft, EDGE_GAP), rightmostLeft),
    ...(dropdownWidth >= available ? { maxWidth: available } : {}),
  };
}
